import {useTranslation} from 'next-i18next';
import Alert from '@/_application/core/components/ui/alert';
import {useAtom} from 'jotai';
import {useRegisterNewAccountByOtp, useRequestValidateVerifyPhone, useVerifyOtp} from '@/framework/user';
import React, {useEffect, useState} from 'react';
import {useModalAction} from '@/_application/core/components/ui/modal/modal.context';
import {isMobile} from 'react-device-detect';
import {standardizePhoneNumber} from '@/lib/phone-utils';
import {Customer, NotificationChannel, ValidatePhoneType} from '__generated__/__types__';
import RegisterForm from '@/_application/modules/auth/components/register-process/register-form';
import {RegisterUserInput} from '@/_application/core/types';
import {useReCaptcha} from '@/_application/modules/recaptcha/hooks/recaptcha';
import {toastSuccessMessage} from '@/_application/core/components/ui/toast/toast';
import LoginFormSwitcher from '@/_application/modules/auth/components/login-form/login-form-switcher';
import useEventTrackingService from '@/_application/modules/tracking/service/event-tracking.service';
import {CustomerDataInput, TrackingTargetProviderEnum} from '@kidslabs/mario-client-event-tracking';
import {getDeviceInformation} from '@/_application/core/utils/device';
import {MetaEventList} from '@/_application/modules/tracking/meta/model/meta-conversion';
import useConversionApi from '@/_application/modules/tracking/meta/tracking-conversion-api';
import PreviousButton from '@/_application/core/components/ui/buttons/previous-button';

import {initialOtpState, otpAtom, otpRequestedTimeAtom} from '../../stores/atom';
import PhoneNumberForm from '../otp/phone-number-form';
import OtpCodeForm from '../otp/code-verify-form';

function Register() {
    const {t} = useTranslation('common');
    const [otpState, setOtpState] = useAtom(otpAtom);
    const [, setOtpRequestedTime] = useAtom(otpRequestedTimeAtom);
    const [otpRegisterLoading, setOtpRegisterLoading] = useState(false);
    const {closeModal, openModal} = useModalAction();
    const {mutate: requestValidateRegisterByPhone, serverError, setServerError} = useRequestValidateVerifyPhone();
    const {mutate: verifyOtp} = useVerifyOtp();
    const {mutate: registerNewAccount} = useRegisterNewAccountByOtp();
    const {retrieveCaptchaToken} = useReCaptcha();
    const {trackSignUpEvent: cdpTrackSignUpEvent} = useEventTrackingService(TrackingTargetProviderEnum.CDP);
    const {sendProcessedDataToConversionAPI} = useConversionApi();

    const doRequestVerifyPhone = async (phoneNumber: string, action: string) => {
        if (otpRegisterLoading) {
            return;
        }

        if (!phoneNumber) {
            setServerError('error-phone-required');
            return;
        }

        setOtpRegisterLoading(true);
        const captchaResponseToken = await retrieveCaptchaToken(action, error => {
            setServerError(error);
        });
        const {data} = await requestValidateRegisterByPhone({
            captchaResponseToken,
            phoneNumber: phoneNumber,
            validateType: ValidatePhoneType.Register,
            notificationChannel: NotificationChannel.Sms,
            sign: '',
        });

        const {requestValidatePhone} = data ?? {};
        if (requestValidatePhone?.success) {
            setOtpRequestedTime(Date.now());
            setOtpState({
                ...otpState,
                step: 'OtpForm',
                secret: requestValidatePhone?.secret!,
                phoneNumber: phoneNumber,
            });
        }
        setOtpRegisterLoading(false);
    };

    const onVerifyPhoneSubmit = async (submitData: {phone_number: string}) => {
        doRequestVerifyPhone(submitData.phone_number, 'requestValidateRegisterByPhone');
    };

    const resendOtp = async () => {
        doRequestVerifyPhone(otpState?.phoneNumber, 'resendOtpRegisterByPhone');
    };

    async function onOtpSubmit(values: any) {
        setOtpRegisterLoading(true);
        const {data} = await verifyOtp({
            phoneNumber: otpState?.phoneNumber,
            secret: otpState?.secret!,
            otp: values.code,
        });
        const {confirmRequestedValidationPhone} = data ?? {};
        const {success, message} = confirmRequestedValidationPhone ?? {};
        if (success) {
            setOtpState({
                ...otpState,
                step: 'RegisterForm',
                secret: confirmRequestedValidationPhone?.secret ?? null,
                otp: values.code,
            });
        } else {
            switch (message) {
                case 'The request secret is not correct':
                    setServerError('text_invalid_request');
                    break;
                case 'Phone number is not requested':
                    setServerError('text_phone_not_found');
                    break;
                case 'The request code is not correct':
                    setServerError('text_invalid_OTP');
                    break;
                case 'The request is expired':
                    setServerError('text_OTP_expire');
                    break;
                default:
                    setServerError(message ?? 'text_error_please_retry');
                    break;
            }
        }

        setOtpRegisterLoading(false);
    }

    async function onRegisterSubmit(registerData: RegisterUserInput) {
        const {email, name} = registerData;
        const phoneNumber = otpState?.phoneNumber;

        const {firstName, lastName} = extractNames(name);
        const deviceRequest = getDeviceInformation();
        const {data} = await registerNewAccount({
            phoneNumber: phoneNumber,
            secret: otpState?.secret!,
            otp: otpState?.otp!,
            accountInfo: {
                email: email,
                first_name: firstName,
                last_name: lastName,
                website_id: 1,
            },
            deviceRequest,
        });
        const {registerNewAccountByOtp} = data ?? {};
        if (registerNewAccountByOtp?.success) {
            // Register successfully
            toastSuccessMessage(t('text_success_register'));
            closeModal();
            openModal('LOGIN_VIEW');

            const customerInput: CustomerDataInput = {
                id: otpState?.phoneNumber,
                name: `${firstName} ${lastName}`,
                email: email,
                phone: otpState?.phoneNumber,
            };
            cdpTrackSignUpEvent({customer: customerInput}).then(() => {});

            sendProcessedDataToConversionAPI(MetaEventList.REGISTRATION, {}, {
                phone_number: otpState?.phoneNumber,
                email: email,
                firstname: firstName,
                lastname: lastName,
            } as Customer);
        }
    }

    const extractNames = (fullName: string) => {
        const words = fullName.split(/\s+/);
        const firstName = words[0];

        let lastName: string;
        if (words.length === 1) {
            lastName = words[0];
        } else {
            lastName = words.slice(1).join(' ');
        }

        return {
            firstName,
            lastName,
        };
    };

    useEffect(() => {
        const timerOTP = setTimeout(() => setServerError(null), 5000);
        return () => {
            clearTimeout(timerOTP);
        };
    }, [serverError]);

    return (
        <div className={`mt-4`}>
            {serverError && (
                <Alert
                    variant="error"
                    message={serverError && t(`${serverError}`)}
                    className="mb-4"
                    closeable={true}
                    onClose={() => setServerError(null)}
                />
            )}
            {otpState.step === 'PhoneNumber' && (
                <div className="flex flex-col items-start justify-center">
                    <p className="mb-7 mt-4 px-4 text-center text-sm leading-relaxed text-body sm:mb-2 sm:mt-2 md:text-base">
                        {t('otp-register-helper')}
                    </p>
                    <div className="w-full px-4">
                        <div className="pb-1 pt-4 text-base font-bold text-primary">{t('phone-number')}</div>
                        <PhoneNumberForm
                            onSubmit={data => onVerifyPhoneSubmit(data)}
                            isLoading={otpRegisterLoading}
                            view="login"
                        />
                    </div>
                </div>
            )}
            {otpState.step === 'OtpForm' && (
                <>
                    <p className="mb-7 mt-4 px-4 text-center text-sm leading-relaxed text-body sm:mb-2 sm:mt-2 md:text-base">
                        {t('otp-login-with-phone', {phone: standardizePhoneNumber(otpState.phoneNumber)})}
                    </p>
                    <OtpCodeForm isLoading={otpRegisterLoading} onSubmit={onOtpSubmit} resendOtp={resendOtp} />
                </>
            )}
            {otpState.step === 'RegisterForm' && <RegisterForm onRegister={onRegisterSubmit} />}
        </div>
    );
}

export default function RegisterProcess() {
    const [counter, setCounter] = React.useState(60);
    const {t} = useTranslation('common');
    const {openModal} = useModalAction();
    const [otpState, setOtpState] = useAtom(otpAtom);

    const changeToLoginForm = () => {
        openModal('LOGIN_VIEW');
    };

    React.useEffect(() => {
        counter > 0 && setTimeout(() => setCounter(counter - 1), 1000);
    }, [counter]);

    const resetStep = () => {
        setOtpState({
            ...initialOtpState,
            operation: 'register',
        });
    };

    useEffect(() => {
        //if user switch from login -> reset step and data to initial
        if (otpState.operation !== 'register') {
            resetStep();
        }
    }, []);

    return (
        <div className="h-screen w-screen flex-col justify-center bg-primary-0 sm:p-8 md:h-auto md:max-w-lg md:rounded-xl">
            <div
                className={`relative ${isMobile ? 'py-4' : ''} flex items-center justify-center text-xl font-bold text-primary`}>
                {t('text-register', 'Đăng ký')}

                {otpState.step === 'OtpForm' && (
                    <div className="absolute left-4 top-0">
                        <PreviousButton onClick={resetStep} />
                    </div>
                )}
            </div>
            <Register />
            {otpState.step === 'PhoneNumber' && <LoginFormSwitcher handleOpenLoginForm={changeToLoginForm} />}
        </div>
    );
}
